import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Modal,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { Plus, BookOpen, Trash2 } from 'lucide-react'
import {
  plansApi,
  type StudentPlanListItem,
  type PlanTemplate,
  type StudentOption,
} from '../../api/plans'

const STATUS_COLORS: Record<string, string> = {
  created: 'blue',
  active: 'green',
  completed: 'gray',
  paused: 'yellow',
}

export function PlanCreation() {
  const navigate = useNavigate()
  const [plans, setPlans] = useState<StudentPlanListItem[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  const [modalOpen, setModalOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [students, setStudents] = useState<StudentOption[]>([])
  const [templates, setTemplates] = useState<PlanTemplate[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null)
  const [removing, setRemoving] = useState(false)

  useEffect(() => {
    fetchPlans(page)
  }, [page])

  function fetchPlans(p: number) {
    setLoading(true)
    plansApi
      .listPlans(p, 15)
      .then((res) => {
        setPlans(res.data.items)
        setTotal(res.data.total)
        setTotalPages(res.data.total_pages)
      })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load plans', color: 'red' }))
      .finally(() => setLoading(false))
  }

  function openModal() {
    setTitle('')
    setSelectedStudentId(null)
    setSelectedTemplateId(null)
    setStudents([])
    setTemplates([])
    setModalOpen(true)

    plansApi.getStudentsWithoutPlan().then((res) => setStudents(res.data)).catch(() => {})
    plansApi.getTemplates().then((res) => setTemplates(res.data)).catch(() => {})
  }

  function handleRemove(planId: number) {
    setRemoving(true)
    plansApi
      .deletePlan(planId)
      .then(() => {
        notifications.show({ title: 'Removed', message: 'Plan removed successfully', color: 'green' })
        setConfirmRemoveId(null)
        fetchPlans(page)
      })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to remove plan', color: 'red' }))
      .finally(() => setRemoving(false))
  }

  function handleSubmit() {
    if (!title.trim()) {
      notifications.show({ title: 'Validation', message: 'Plan title is required', color: 'orange' })
      return
    }
    if (!selectedStudentId) {
      notifications.show({ title: 'Validation', message: 'Please select a student', color: 'orange' })
      return
    }
    if (!selectedTemplateId) {
      notifications.show({ title: 'Validation', message: 'Please select a plan type', color: 'orange' })
      return
    }

    setSubmitting(true)
    plansApi
      .createPlan({
        name: title.trim(),
        student_id: Number(selectedStudentId),
        template_id: Number(selectedTemplateId),
      })
      .then(() => {
        notifications.show({ title: 'Success', message: 'Plan created successfully', color: 'green' })
        setModalOpen(false)
        setPage(1)
        fetchPlans(1)
      })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to create plan', color: 'red' }))
      .finally(() => setSubmitting(false))
  }

  const studentOptions = students.map((s) => ({
    value: String(s.id),
    label: `${s.user_name} (${s.email})`,
  }))

  const templateOptions = templates.map((t) => ({
    value: String(t.id),
    label: `${t.name} — ${t.duration_type}`,
  }))

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Box>
            <Title order={2} c="var(--sat-text)">Learning Plans</Title>
            <Text c="var(--sat-text-muted)" mt={4}>
              {total} active plan{total !== 1 ? 's' : ''}
            </Text>
          </Box>
          <Button leftSection={<Plus size={16} />} onClick={openModal} color="blue" radius="xl">
            Create New Plan
          </Button>
        </Group>

        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : plans.length === 0 ? (
          <Box ta="center" py="xl" c="var(--sat-text-muted)">
            <BookOpen size={48} />
            <Text mt="md">No plans yet. Create one to get started.</Text>
          </Box>
        ) : (
          <Stack gap="md">
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Student</Table.Th>
                  <Table.Th>Template</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Actions</Table.Th>
                  <Table.Th>Created</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {plans.map((plan) => (
                  <Table.Tr key={plan.id}>
                    <Table.Td fw={500}>
                      <Anchor
                        component="button"
                        fw={600}
                        onClick={() => navigate(`/tutor/plans/${plan.id}`)}
                        style={{ textAlign: 'left' }}
                      >
                        {plan.name}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>{plan.student_name ?? `Student #${plan.student_id}`}</Table.Td>
                    <Table.Td c="var(--sat-text-muted)">{plan.template_name ?? '—'}</Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_COLORS[plan.status] ?? 'gray'} variant="light" size="sm">
                        {plan.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        radius="xl"
                        leftSection={<Trash2 size={12} />}
                        onClick={() => setConfirmRemoveId(plan.id)}
                      >
                        Remove
                      </Button>
                    </Table.Td>
                    <Table.Td>
                      <Text c="var(--sat-text-muted)" size="sm">
                        {plan.create_datetime
                          ? new Date(plan.create_datetime).toLocaleDateString()
                          : '—'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {totalPages > 1 && (
              <Group justify="center">
                <Pagination total={totalPages} value={page} onChange={setPage} radius="xl" />
              </Group>
            )}
          </Stack>
        )}
      </Stack>

      <Modal
        opened={confirmRemoveId !== null}
        onClose={() => setConfirmRemoveId(null)}
        title="Remove Plan"
        radius="xl"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            This will permanently remove the plan along with all its sessions and activities. This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setConfirmRemoveId(null)} radius="xl">
              Cancel
            </Button>
            <Button
              color="red"
              loading={removing}
              onClick={() => confirmRemoveId !== null && handleRemove(confirmRemoveId)}
              radius="xl"
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Create New Learning Plan"
        radius="xl"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Plan Title"
            placeholder="e.g. Sprint 2026 SAT Prep"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            required
            radius="md"
          />
          <Group grow align="flex-start">
            <Select
              label="Student"
              placeholder="Select student"
              data={studentOptions}
              value={selectedStudentId}
              onChange={setSelectedStudentId}
              searchable
              required
              radius="md"
              nothingFoundMessage="No students without a plan"
            />
            <Select
              label="Plan Type"
              placeholder="Select template"
              data={templateOptions}
              value={selectedTemplateId}
              onChange={setSelectedTemplateId}
              searchable
              required
              radius="md"
              nothingFoundMessage="No templates available"
            />
          </Group>
          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={() => setModalOpen(false)} radius="xl">
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting} radius="xl">
              Create Plan
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  )
}
